import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { Plus, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Banner } from '@/components/Banner';
import { BotonEnlace, Button } from '@/components/Button';
import { Card, EstadoVacio, Seccion } from '@/components/Card';
import { Casilla } from '@/components/Casilla';
import { DateField, NumberField, TextField } from '@/components/Field';
import { Fila, Filas } from '@/components/Fila';
import { GraficaSerie } from '@/components/GraficaSerie';
import { UnitField } from '@/components/UnitField';
import { leerSettings } from '@/db/schema';
import {
  borrarMedida,
  contarMedidasIlegibles,
  corregirMedida,
  guardarMedidas,
  leerMedidas,
  ocultarMedida,
  type MedidaACapturar,
} from '@/db/medidas';
import { nuevoId } from '@/db/ids';
import { mensajeDeError } from '@/db/tx';
import type { Measurement, Settings, TipoMedida } from '@/db/types';
import type { FechaLocal } from '@/domain/dates';
import { fechaCorta, fechaDelDia } from '@/domain/hoy';
import {
  NOMBRE_MEDIDA,
  NOTA_MEDIDA_MAX,
  TIPOS_DE_MEDIDA,
  esLongitud,
  esTipoMedida,
  formatearCambioDeMedida,
  formatearMedida,
  leerMedidaEscrita,
  puntosDeMedida,
  resumenDeMedida,
  unidadPropuestaDeMedida,
} from '@/domain/medidas';
import { formatearNumero } from '@/domain/numbers';
import { desdeCm, esUnidadLongitud, formatearCapturado, type UnidadLongitud } from '@/domain/units';
import { useFechaHoy } from '@/lib/useFechaHoy';

/*
 * Medidas corporales (Req. 17; tarea 19, 0.4.1): registrar varias a la vez,
 * ocultar las que no se usan, el historial y la gráfica de cada una con su
 * cambio contra la primera y la anterior, y corregir o borrar.
 */

const VOLVER_A_CUERPO = { a: '/cuerpo', texto: 'Cuerpo', etiqueta: 'Volver a Cuerpo' };
const VOLVER_A_MEDIDAS = { a: '/cuerpo/medidas', texto: 'Medidas', etiqueta: 'Volver a tus medidas' };

/** Un número como se escribe en un campo, sin recortar lo guardado. */
const paraCampo = (v: number) => formatearNumero(v, { decimales: 6, fijos: false }).replace(/,/g, '');
const minuscula = (t: string) => t.toLocaleLowerCase('es-MX');

/** "escrita 31.5 in", si se escribió en otra unidad que la que se muestra (Req. 5.8). */
function escritaEnOtra(m: Measurement, unidad: UnidadLongitud): string | null {
  const c = m.capturado;
  if (c === null || !esUnidadLongitud(c.unidad) || c.unidad === unidad || !Number.isFinite(c.valor)) return null;
  return `escrita ${formatearCapturado(c)}`;
}

function NoEsta({ texto, volver }: { texto: string; volver: { a: string; texto: string; etiqueta: string } }) {
  return (
    <div>
      <PageHeader titulo="No está." volver={volver} />
      <EstadoVacio titulo={texto} descripcion="Puede que se haya borrado." />
    </div>
  );
}

/** Medidas guardadas que no se pueden leer: no se muestran y siguen guardadas tal cual. */
function AvisoMedidasIlegibles({ cantidad }: { cantidad: number }) {
  if (cantidad === 0) return null;
  const una = cantidad === 1;
  return (
    <div className="mb-6">
      <Banner tono="aviso" titulo={una ? '1 medida no se puede leer.' : `${cantidad} medidas no se pueden leer.`}>
        {`${una ? 'Tiene' : 'Tienen'} la fecha, el tipo o el valor dañados (pudo llegar así en un respaldo): no se ${
          una ? 'muestra' : 'muestran'
        }, y ${una ? 'sigue guardada' : 'siguen guardadas'} tal cual.`}
      </Banner>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tus medidas: la última de cada una y las que se usan (Req. 17.3)
// ---------------------------------------------------------------------------

export function MedidasPage() {
  const ajustes = useLiveQuery(() => leerSettings(), []);
  const medidas = useLiveQuery(() => leerMedidas(), []);
  const ilegibles = useLiveQuery(() => contarMedidasIlegibles(), []);
  const [error, setError] = useState<string | null>(null);
  if (ajustes === undefined || medidas === undefined || ilegibles === undefined) return null;

  const unidad = ajustes.unidadLongitud;
  const ocultas = new Set(ajustes.medidasOcultas);
  const deTipo = (t: TipoMedida) => medidas.filter((m) => m.tipo === t);
  const visibles = TIPOS_DE_MEDIDA.filter((t) => !ocultas.has(t));
  const ocultasConHistorial = TIPOS_DE_MEDIDA.filter((t) => ocultas.has(t) && deTipo(t).length > 0);

  async function cambiar(tipo: TipoMedida, oculta: boolean) {
    setError(null);
    try {
      await ocultarMedida(tipo, oculta);
    } catch (e) {
      setError(mensajeDeError(e));
    }
  }

  const fila = (t: TipoMedida) => {
    const r = resumenDeMedida(deTipo(t));
    const detalle =
      r === null
        ? 'Sin medidas todavía'
        : [
            formatearMedida(r.ultima.valor, t, unidad),
            minuscula(fechaCorta(r.ultima.fecha)),
            r.cambioDesdePrimera === null ? null : `${formatearCambioDeMedida(r.cambioDesdePrimera, t, unidad)} desde la primera`,
          ]
            .filter(Boolean)
            .join(' · ');
    return <Fila key={t} titulo={NOMBRE_MEDIDA[t]} detalle={detalle} a={`/cuerpo/medidas/${t}`} />;
  };

  return (
    <div>
      <PageHeader
        titulo="Tus medidas."
        descripcion="El progreso no siempre se ve en la báscula. Toca una medida para ver su historial y su gráfica."
        volver={VOLVER_A_CUERPO}
      />
      <AvisoMedidasIlegibles cantidad={ilegibles} />
      {visibles.length > 0 ? (
        <BotonEnlace to="/cuerpo/medidas/nuevas" icono={Plus}>
          Registrar medidas
        </BotonEnlace>
      ) : (
        <p className="text-[13px] leading-relaxed text-texto-suave">Ocultaste todas: marca en «Las que usas» las que quieras registrar.</p>
      )}
      {visibles.length > 0 && (
        <div className="mt-6">
          <Filas>{visibles.map(fila)}</Filas>
        </div>
      )}
      {ocultasConHistorial.length > 0 && (
        <div className="mt-8">
          <Seccion titulo="Ocultas, con historial" descripcion="No se piden al registrar; sus medidas se conservan.">
            <Filas>{ocultasConHistorial.map(fila)}</Filas>
          </Seccion>
        </div>
      )}
      <div className="mt-8">
        <Seccion titulo="Las que usas" descripcion="Las que desmarques ya no se piden al registrar. Sus medidas no se borran.">
          <div className="grid gap-2">
            {TIPOS_DE_MEDIDA.map((t) => (
              <Casilla key={t} marcada={!ocultas.has(t)} onCambio={(marcada) => void cambiar(t, !marcada)}>
                {NOMBRE_MEDIDA[t]}
              </Casilla>
            ))}
          </div>
        </Seccion>
      </div>
      {error !== null && (
        <div className="mt-4">
          <Banner tono="alerta" titulo="No se pudo">
            {error}
          </Banner>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Registrar varias a la vez (Req. 17.1, 17.2, 17.6)
// ---------------------------------------------------------------------------

export function CapturaMedidasPage() {
  const hoy = useFechaHoy();
  const ajustes = useLiveQuery(() => leerSettings(), []);
  const medidas = useLiveQuery(() => leerMedidas(), []);
  if (ajustes === undefined || medidas === undefined) return null;
  return <FormularioCaptura hoy={hoy} ajustes={ajustes} medidas={medidas} />;
}

function FormularioCaptura({ hoy, ajustes, medidas }: { hoy: FechaLocal; ajustes: Settings; medidas: readonly Measurement[] }) {
  const navegar = useNavigate();
  // Las que se piden se fijan al abrir, y los ids también: dos toques no duplican (Req. 1.9)
  const [tipos] = useState(() => TIPOS_DE_MEDIDA.filter((t) => !ajustes.medidasOcultas.includes(t)));
  const [ids] = useState(() => Object.fromEntries(TIPOS_DE_MEDIDA.map((t) => [t, nuevoId('med')])) as Record<TipoMedida, string>);
  const [unidades, setUnidades] = useState(
    () =>
      Object.fromEntries(
        TIPOS_DE_MEDIDA.map((t) => [t, unidadPropuestaDeMedida(medidas.filter((m) => m.tipo === t), ajustes.unidadLongitud)]),
      ) as Record<TipoMedida, UnidadLongitud>,
  );
  const [fecha, setFecha] = useState<FechaLocal>(hoy);
  const [textos, setTextos] = useState<Partial<Record<TipoMedida, string>>>({});
  const [nota, setNota] = useState('');
  const [errores, setErrores] = useState<Partial<Record<TipoMedida, string>>>({});
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  const limpiar = (t?: TipoMedida) => {
    setError(null);
    if (t !== undefined) setErrores((e) => ({ ...e, [t]: undefined }));
  };

  /** Lo de ese tipo que ya hay: la de este día (se reemplaza) o la anterior, para comparar. */
  function ayuda(t: TipoMedida): string | undefined {
    const deEseDia = medidas.find((m) => m.tipo === t && m.fecha === fecha);
    if (deEseDia !== undefined) return `Ya tienes ${formatearMedida(deEseDia.valor, t, unidades[t])} de este día: al guardar se reemplaza.`;
    // Vienen de la más reciente a la más antigua
    const previa = medidas.find((m) => m.tipo === t && m.fecha < fecha);
    return previa === undefined ? undefined : `La anterior: ${formatearMedida(previa.valor, t, unidades[t])} (${minuscula(fechaCorta(previa.fecha))}).`;
  }

  async function guardar() {
    if (guardando) return;
    setError(null);
    const conError: Partial<Record<TipoMedida, string>> = {};
    const listas: MedidaACapturar[] = [];
    for (const t of tipos) {
      const r = leerMedidaEscrita(textos[t] ?? '', t, unidades[t]);
      if (r.tipo === 'invalida') conError[t] = r.error;
      else if (r.tipo === 'valida') listas.push({ id: ids[t], tipo: t, valor: r.valor, unidad: unidades[t] });
    }
    setErrores(conError);
    if (Object.keys(conError).length > 0) {
      setError('Revisa las medidas marcadas: no se guardó ninguna.');
      return;
    }
    if (listas.length === 0) {
      setError('Escribe al menos una medida. Las vacías no se guardan.');
      return;
    }
    setGuardando(true);
    try {
      await guardarMedidas({ fecha, nota, medidas: listas }, hoy);
      void navegar('/cuerpo/medidas', { replace: true });
    } catch (e) {
      setError(mensajeDeError(e));
      setGuardando(false);
    }
  }

  return (
    <div>
      <PageHeader
        antetitulo={fechaDelDia(fecha)}
        titulo="Registrar medidas."
        descripcion="Escribe solo las que tomaste: las vacías no se guardan. Cada una en cm o in; la grasa corporal, en %."
        volver={VOLVER_A_MEDIDAS}
      />
      {tipos.length === 0 ? (
        <EstadoVacio titulo="Ocultaste todas tus medidas." descripcion="Marca en Tus medidas → «Las que usas» las que quieras registrar.">
          <BotonEnlace to="/cuerpo/medidas" variante="secundario" reemplazar>
            Ir a tus medidas
          </BotonEnlace>
        </EstadoVacio>
      ) : (
        <form
          className="space-y-5"
          onSubmit={(e) => {
            e.preventDefault();
            void guardar();
          }}
        >
          <DateField
            etiqueta="Fecha"
            valor={fecha}
            onCambio={(v) => {
              setFecha(v);
              limpiar();
            }}
            max={hoy}
          />
          {tipos.map((t) =>
            esLongitud(t) ? (
              <UnitField
                key={t}
                etiqueta={NOMBRE_MEDIDA[t]}
                magnitud="longitud"
                valor={textos[t] ?? ''}
                onCambio={(v) => {
                  setTextos((x) => ({ ...x, [t]: v }));
                  limpiar(t);
                }}
                unidad={unidades[t]}
                onCambioUnidad={(u) => {
                  setUnidades((x) => ({ ...x, [t]: u }));
                  limpiar(t);
                }}
                unidadPreferida={ajustes.unidadLongitud}
                error={errores[t] ?? null}
                ayuda={ayuda(t)}
              />
            ) : (
              <NumberField
                key={t}
                etiqueta="Grasa corporal (%)"
                valor={textos[t] ?? ''}
                onCambio={(v) => {
                  setTextos((x) => ({ ...x, [t]: v }));
                  limpiar(t);
                }}
                error={errores[t] ?? null}
                ayuda={ayuda(t)}
              />
            ),
          )}
          <TextField
            etiqueta="Nota (opcional)"
            valor={nota}
            onCambio={(v) => {
              setNota(v);
              limpiar();
            }}
            maxLength={NOTA_MEDIDA_MAX}
            placeholder="Por ejemplo: por la mañana, en ayunas"
          />
          {error !== null && (
            <Banner tono="alerta" titulo="No se guardó">
              {error}
            </Banner>
          )}
          <Button type="submit" disabled={guardando}>
            {guardando ? 'Guardando…' : 'Guardar medidas'}
          </Button>
        </form>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Historial y gráfica de una medida (Req. 17.4)
// ---------------------------------------------------------------------------

export function MedidaTipoPage() {
  const { tipo = '' } = useParams();
  const hoy = useFechaHoy();
  const ajustes = useLiveQuery(() => leerSettings(), []);
  const medidas = useLiveQuery(() => leerMedidas(), []);
  if (ajustes === undefined || medidas === undefined) return null;
  if (!esTipoMedida(tipo)) return <NoEsta texto="Esa medida no existe." volver={VOLVER_A_MEDIDAS} />;

  const unidad = ajustes.unidadLongitud;
  const nombre = NOMBRE_MEDIDA[tipo];
  // De la más reciente a la más antigua
  const suyas = medidas.filter((m) => m.tipo === tipo);
  const r = resumenDeMedida(suyas);
  const oculta = ajustes.medidasOcultas.includes(tipo);
  const unidadEje = esLongitud(tipo) ? unidad : '%';
  const formatear = (v: number) => `${formatearNumero(v, { decimales: 1 })} ${unidadEje}`;
  const cambio = (v: number) => {
    const texto = formatearNumero(v, { decimales: 1 });
    return v > 0 && texto !== '0.0' ? `+${texto} ${unidadEje}` : `${texto} ${unidadEje}`;
  };

  return (
    <div>
      <PageHeader
        antetitulo="Medidas"
        titulo={`${nombre}.`}
        {...(oculta ? { descripcion: 'Oculta: no se pide al registrar. Su historial se conserva.' } : {})}
        volver={VOLVER_A_MEDIDAS}
      />
      {r === null ? (
        <EstadoVacio titulo={`Sin medidas de ${minuscula(nombre)} todavía.`} descripcion="La primera que registres aparecerá aquí, con su gráfica.">
          <BotonEnlace to="/cuerpo/medidas/nuevas" variante="secundario" icono={Plus}>
            Registrar medidas
          </BotonEnlace>
        </EstadoVacio>
      ) : (
        <>
          <Card>
            <span className="antetitulo">Última</span>
            <p className="mt-2 text-[34px] leading-none tracking-[-1px] tabular-nums text-texto">{formatearMedida(r.ultima.valor, tipo, unidad)}</p>
            <p className="mt-2 text-[13px] leading-relaxed text-texto-suave">
              {[fechaCorta(r.ultima.fecha), escritaEnOtra(r.ultima, unidad)].filter(Boolean).join(' · ')}
            </p>
            {r.anterior === null || r.cambioDesdePrimera === null || r.cambioDesdeAnterior === null ? (
              <p className="mt-4 border-t border-borde pt-4 text-[13px] leading-relaxed text-texto-suave">
                Con la siguiente verás cuánto cambió contra esta.
              </p>
            ) : (
              <dl className="mt-4 grid grid-cols-2 gap-3 border-t border-borde pt-4">
                <div className="min-w-0">
                  <dt className="text-[12px] text-texto-suave">Contra la primera</dt>
                  <dd className="mt-1">
                    <span className="block text-[17px] tabular-nums text-texto">{formatearCambioDeMedida(r.cambioDesdePrimera, tipo, unidad)}</span>
                    <span className="block text-[12px] text-texto-suave">{fechaCorta(r.primera.fecha)}</span>
                  </dd>
                </div>
                <div className="min-w-0">
                  <dt className="text-[12px] text-texto-suave">Contra la anterior</dt>
                  <dd className="mt-1">
                    <span className="block text-[17px] tabular-nums text-texto">{formatearCambioDeMedida(r.cambioDesdeAnterior, tipo, unidad)}</span>
                    <span className="block text-[12px] text-texto-suave">{fechaCorta(r.anterior.fecha)}</span>
                  </dd>
                </div>
              </dl>
            )}
          </Card>

          <div className="mt-[29px]">
            <Seccion titulo="En el tiempo" descripcion="Cada punto es la medida de ese día.">
              <GraficaSerie
                etiqueta={`Periodo de la gráfica de ${minuscula(nombre)}`}
                puntos={puntosDeMedida(suyas, tipo, unidad)}
                nombrePuntos={nombre}
                formatear={formatear}
                formatearCambio={cambio}
                textoCantidad={(n) => (n === 1 ? '1 medida' : `${n} medidas`)}
                unidad={unidadEje}
                vacio="Sin medidas en este periodo. Elige uno más largo para ver las anteriores."
                hoy={hoy}
                rangoInicial="todo"
              />
            </Seccion>
          </div>

          <div className="mt-8">
            <Seccion titulo="Todas" extra={suyas.length === 1 ? '1 medida' : `${suyas.length} medidas`} descripcion="Toca una para corregirla o borrarla.">
              <Filas>
                {suyas.map((m) => (
                  <Fila
                    key={m.id}
                    titulo={formatearMedida(m.valor, tipo, unidad)}
                    detalle={[fechaCorta(m.fecha), escritaEnOtra(m, unidad), m.nota].filter(Boolean).join(' · ')}
                    a={`/cuerpo/medida/${m.id}`}
                  />
                ))}
              </Filas>
            </Seccion>
          </div>
          <div className="mt-6">
            <BotonEnlace to="/cuerpo/medidas/nuevas" variante="secundario" icono={Plus}>
              Registrar medidas
            </BotonEnlace>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Corregir o borrar una medida (Req. 17.5)
// ---------------------------------------------------------------------------

export function MedidaPage() {
  const { id = '' } = useParams();
  const hoy = useFechaHoy();
  const ajustes = useLiveQuery(() => leerSettings(), []);
  const medidas = useLiveQuery(() => leerMedidas(), []);
  if (ajustes === undefined || medidas === undefined) return null;
  const medida = medidas.find((m) => m.id === id);
  if (medida === undefined) return <NoEsta texto="Esa medida ya no existe." volver={VOLVER_A_MEDIDAS} />;
  return <FormularioMedida key={medida.id} medida={medida} hoy={hoy} ajustes={ajustes} />;
}

function FormularioMedida({ medida, hoy, ajustes }: { medida: Measurement; hoy: FechaLocal; ajustes: Settings }) {
  const navegar = useNavigate();
  const tipo = medida.tipo;
  const longitud = esLongitud(tipo);
  const capturado = medida.capturado !== null && esUnidadLongitud(medida.capturado.unidad) && Number.isFinite(medida.capturado.valor) ? medida.capturado : null;
  const unidadInicial: UnidadLongitud = capturado !== null && esUnidadLongitud(capturado.unidad) ? capturado.unidad : ajustes.unidadLongitud;
  // Lo escrito, sin recortarlo; sin lo capturado, el valor en la unidad preferida
  const textoInicial = paraCampo(!longitud ? medida.valor : (capturado?.valor ?? desdeCm(medida.valor, unidadInicial)));
  const notaInicial = typeof medida.nota === 'string' ? medida.nota : '';
  const [fecha, setFecha] = useState<FechaLocal>(medida.fecha);
  const [texto, setTexto] = useState(textoInicial);
  const [unidad, setUnidad] = useState<UnidadLongitud>(unidadInicial);
  const [nota, setNota] = useState(notaInicial);
  const [errorValor, setErrorValor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [confirmarBorrar, setConfirmarBorrar] = useState(false);
  const volverA = `/cuerpo/medidas/${tipo}`;

  async function guardar() {
    if (guardando) return;
    setError(null);
    const valorSinCambiar = texto.trim() === textoInicial && unidad === unidadInicial;
    // Sin cambios no se escribe nada: queda exactamente como estaba
    if (valorSinCambiar && fecha === medida.fecha && nota.trim() === notaInicial.trim()) {
      void navegar(volverA, { replace: true });
      return;
    }
    const leido = leerMedidaEscrita(texto, tipo, unidad);
    if (leido.tipo !== 'valida') {
      setErrorValor(leido.tipo === 'vacia' ? 'Escribe la medida. Para quitarla, bórrala.' : leido.error);
      return;
    }
    // El valor sin tocar se guarda tal como se escribió, sin redondear
    const valor = valorSinCambiar ? (longitud ? (capturado?.valor ?? leido.valor) : medida.valor) : leido.valor;
    setGuardando(true);
    try {
      await corregirMedida(medida.id, { fecha, valor, unidad, nota }, hoy);
      void navegar(volverA, { replace: true });
    } catch (e) {
      setError(mensajeDeError(e));
      setGuardando(false);
    }
  }

  async function borrar() {
    setGuardando(true);
    try {
      await borrarMedida(medida.id);
      void navegar(volverA, { replace: true });
    } catch (e) {
      setError(mensajeDeError(e));
      setGuardando(false);
    }
  }

  return (
    <div>
      <PageHeader
        antetitulo={NOMBRE_MEDIDA[tipo]}
        titulo="Corregir medida."
        descripcion="Corregirla cambia su historial y su gráfica; lo demás no se toca."
        volver={{ a: volverA, texto: NOMBRE_MEDIDA[tipo], etiqueta: `Volver a ${minuscula(NOMBRE_MEDIDA[tipo])}` }}
      />
      {capturado !== null && capturado.unidad !== ajustes.unidadLongitud && (
        <p className="-mt-3 mb-5 text-[13px] text-texto-suave">
          Escrita: {formatearCapturado(capturado)} · se ve como {formatearMedida(medida.valor, tipo, ajustes.unidadLongitud)}
        </p>
      )}
      <form
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          void guardar();
        }}
      >
        <DateField
          etiqueta="Fecha"
          valor={fecha}
          onCambio={(v) => {
            setFecha(v);
            setError(null);
          }}
          max={hoy}
        />
        {longitud ? (
          <UnitField
            etiqueta={NOMBRE_MEDIDA[tipo]}
            magnitud="longitud"
            valor={texto}
            onCambio={(v) => {
              setTexto(v);
              setErrorValor(null);
            }}
            unidad={unidad}
            onCambioUnidad={(u) => {
              setUnidad(u);
              setErrorValor(null);
            }}
            unidadPreferida={ajustes.unidadLongitud}
            error={errorValor}
          />
        ) : (
          <NumberField
            etiqueta="Grasa corporal (%)"
            valor={texto}
            onCambio={(v) => {
              setTexto(v);
              setErrorValor(null);
            }}
            error={errorValor}
          />
        )}
        <TextField etiqueta="Nota (opcional)" valor={nota} onCambio={setNota} maxLength={NOTA_MEDIDA_MAX} />
        {error !== null && (
          <Banner tono="alerta" titulo="No se guardó">
            {error}
          </Banner>
        )}
        <Button type="submit" disabled={guardando}>
          {guardando ? 'Guardando…' : 'Guardar cambios'}
        </Button>
      </form>
      <div className="mt-6 space-y-2">
        {confirmarBorrar ? (
          <div className="space-y-2 rounded-panel bg-alerta-suave p-4">
            <p className="text-[13px] text-texto">¿Borrar esta medida? No se puede deshacer; las demás no cambian.</p>
            <div className="grid grid-cols-2 gap-2">
              <Button variante="peligro" disabled={guardando} onClick={() => void borrar()}>
                Sí, borrar
              </Button>
              <Button variante="secundario" onClick={() => setConfirmarBorrar(false)}>
                Cancelar
              </Button>
            </div>
          </div>
        ) : (
          <Button variante="fantasma" icono={Trash2} onClick={() => setConfirmarBorrar(true)}>
            Borrar medida
          </Button>
        )}
      </div>
    </div>
  );
}
